import {
  CloudWatchLogsClient,
  CreateLogStreamCommand,
  DescribeLogStreamsCommand,
  InputLogEvent,
  PutLogEventsCommand,
  PutLogEventsCommandInput,
} from '@aws-sdk/client-cloudwatch-logs'
import {
  CognitoIdentityCredentialProvider,
  CognitoIdentityCredentials,
  fromCognitoIdentityPool,
} from '@aws-sdk/credential-provider-cognito-identity'
import { Mutex } from 'async-mutex'
import { captureException } from '~/services/sentry'
import { getCognitoIdentityPoolId } from '~/utils/common'

const ID_POOL = getCognitoIdentityPoolId()
const REGION = 'us-west-2'
const SEND_DELAY = 10000 // 10 seconds

// AWS CloudWatch limits. Both caps are set below AWS's own so that any
// discrepancy between its accounting and ours costs headroom, not a rejected
// batch: AWS rejects the whole batch when either limit is exceeded.
const MAX_LOG_EVENT_SIZE = 250_000 // AWS hard cap is 262,144 bytes
const MAX_BATCH_SIZE = 1_000_000 // AWS hard cap is 1,048,576 bytes
const MAX_EVENTS_PER_BATCH = 10000
const AWS_EVENT_OVERHEAD = 26 // AWS charges 26 bytes per log event
const MAX_MESSAGE_SIZE = MAX_LOG_EVENT_SIZE - AWS_EVENT_OVERHEAD

// Attempts an individual event gets before it is dropped. Counted per event
// rather than per send, because a re-queued batch is resent alongside whatever
// arrived meanwhile: a shared counter drops those newer events on the older
// batch's last strike. Without any cap, a batch AWS will never accept is
// resent every SEND_DELAY forever, blocking every log queued behind it.
const MAX_SEND_ATTEMPTS = 3

// How long an environmental failure — offline, throttled, no response — may keep
// a batch alive. Bounded by wall clock rather than attempts because flush() runs
// on component unmount and can drive far more sends than one per SEND_DELAY,
// which would otherwise burn an attempt budget in seconds.
const MAX_TRANSIENT_MS = 60 * 60 * 1000 // 1 hour

// Error codes meaning AWS rejected the contents of the batch. Deliberately a
// short allow-list rather than a status-code range: MAX_SEND_ATTEMPTS already
// bounds everything else, so the only cost of missing a code here is a few
// wasted retries, whereas wrongly classifying a recoverable error (an expired
// token is also a 4xx) throws away logs that would have sent on the next try.
const TERMINAL_ERROR_CODES = [
  'InvalidParameter',
  'Validation',
  'MalformedQuery',
]

// AWS accepted the batch already, so resending it would duplicate the events.
const ALREADY_ACCEPTED_CODES = ['DataAlreadyAccepted']

// Fixed by clearing the cached credentials and sending again.
const CREDENTIAL_ERROR_CODES = ['ExpiredToken', 'InvalidSignature']

// The batch is well-formed and AWS is asking us to slow down, so these get the
// transient ceiling. Listed as codes as well as read from $retryable, since the
// raw wire shape carries no $retryable.
const THROTTLING_ERROR_CODES = [
  'Throttling',
  'TooManyRequests',
  'RequestLimitExceeded',
]

// Shared because encode()/decode() are stateless and a batch can size 10k
// messages at once.
const textEncoder = new TextEncoder()
const textDecoder = new TextDecoder('utf-8')

const utf8Length = (str: string): number => textEncoder.encode(str).byteLength

/**
 * Truncates a string so its UTF-8 byte length does not exceed maxBytes.
 * substring() would cut by UTF-16 code unit, which can still leave a message
 * several times over the AWS byte limit once it contains non-ASCII text.
 */
function truncateToByteLength(str: string, maxBytes: number): string {
  const encoded = textEncoder.encode(str)
  if (encoded.byteLength <= maxBytes) return str

  // Walk back off any continuation bytes (0b10xxxxxx) so we never split a character.
  let boundary = maxBytes
  while (boundary > 0 && (encoded[boundary] & 0xc0) === 0x80) {
    boundary--
  }

  return textDecoder.decode(encoded.subarray(0, boundary))
}

/**
 * The service error code, whichever field the SDK put it in. name is only set
 * when the response deserialized into a modeled exception class; __type carries
 * the wire code either way, so prefer it. Every classifier below has to go
 * through here — matching on name alone misses the raw-__type shape.
 */
function errorCode(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined

  const { name, __type } = err as { name?: string; __type?: string }
  return __type ?? name
}

// Substring match because __type may arrive namespaced, as in
// 'com.amazonaws.logs#InvalidParameterException'.
function hasErrorCode(err: unknown, codes: string[]): boolean {
  const code = errorCode(err)
  return code !== undefined && codes.some((c) => code.includes(c))
}

/**
 * True when AWS rejected the batch itself rather than the attempt to send it.
 */
function isBatchRejected(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false

  const { $retryable, $metadata } = err as {
    $retryable?: { throttling?: boolean }
    $metadata?: unknown
  }
  // The SDK's own hint wins when the deserializer populated it.
  if ($retryable) return false

  // Only the service can reject a batch, and only a service error carries
  // $metadata. Without this an error thrown locally mid-send is dropped on the
  // first try whenever its name happens to contain one of the codes below —
  // 'ValidationError' matches 'Validation'.
  if ($metadata === undefined) return false

  return hasErrorCode(err, TERMINAL_ERROR_CODES)
}

/**
 * True when fresh credentials would let the same batch through.
 */
function isCredentialError(err: unknown): boolean {
  if (hasErrorCode(err, CREDENTIAL_ERROR_CODES)) return true

  // Fallback for errors that carry no usable code, only prose.
  const { message } = (err ?? {}) as { message?: string }
  return (
    message !== undefined &&
    (message.includes('expired') ||
      message.includes('ExpiredTokenException') ||
      message.includes('Signature expired'))
  )
}

// Both fields are internal bookkeeping and are stripped before the batch is
// sent. They are tracked separately so that time spent waiting out an outage
// does not consume the allowance for failures the service actually answered.
type BufferedEvent = InputLogEvent & {
  attempts?: number
  transientSince?: number
}

const toLogEvents = (logs: BufferedEvent[]): InputLogEvent[] =>
  logs.map(({ message, timestamp }) => ({ message, timestamp }))

/**
 * True when the send failed for a reason outside the batch, so the same events
 * will go through once the condition clears. These are bounded by
 * MAX_TRANSIENT_MS rather than MAX_SEND_ATTEMPTS.
 */
function isTransientFailure(err: unknown): boolean {
  // Trustworthy when it is false, but not the other way around: Chromium
  // reports true whenever any interface is up, so a captive portal or a Wi-Fi
  // link with no route to the internet still reads as online. The $metadata
  // check below is what actually covers those.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true
  }

  if (typeof err !== 'object' || err === null) return false

  const { $retryable, $metadata } = err as {
    $retryable?: { throttling?: boolean }
    $metadata?: unknown
  }

  if ($retryable?.throttling === true) return true
  if (hasErrorCode(err, THROTTLING_ERROR_CODES)) return true

  // Every service error carries $metadata, so its absence means the request
  // never got a response.
  return $metadata === undefined
}

/**
 * Sends logs to CloudWatch when 10 seconds have elapsed.
 */
class CloudWatchLogger {
  // One instance per destination rather than one overall. The buffer outlives a
  // single send cycle, so a shared instance whose stream name is reassigned
  // ships whatever is still buffered to the new stream — one user's logs into
  // another user's stream. Entries are inert once their buffer drains, and the
  // key space is the handful of pbx/user pairs seen in a session.
  private static _instances = new Map<string, CloudWatchLogger>()
  private readonly _logGroupName: string
  private readonly _logStreamName: string
  private _client: CloudWatchLogsClient
  private _credentialProvider: CognitoIdentityCredentialProvider
  private _cachedCredentials: CognitoIdentityCredentials | null = null
  private _logStreamExists = false
  private _mutex: Mutex
  private _isSending = false

  private _logBuffer: BufferedEvent[] = []
  private _sendTimer: ReturnType<typeof setTimeout> | null = null

  private constructor(logGroupName: string, logStreamName: string) {
    this._mutex = new Mutex()
    this._logGroupName = logGroupName
    this._logStreamName = logStreamName

    this._credentialProvider = fromCognitoIdentityPool({
      identityPoolId: ID_POOL,
      clientConfig: { region: REGION },
    })

    this._client = new CloudWatchLogsClient({
      region: REGION,
      credentials: this._credentialProvider,
    })
  }

  public static getInstance(
    logGroupName: string,
    logStreamName: string,
  ): CloudWatchLogger {
    const key = `${logGroupName}\n${logStreamName}`
    let instance = CloudWatchLogger._instances.get(key)
    if (instance === undefined) {
      instance = new CloudWatchLogger(logGroupName, logStreamName)
      CloudWatchLogger._instances.set(key, instance)
    }
    return instance
  }

  public async log(message: string): Promise<void> {
    const release = await this._mutex.acquire()
    try {
      // In range by construction. sendLogs() re-checks against AWS's window,
      // where the clock has had time to move.
      const timestamp = Date.now()

      // Truncate message if too large (measure UTF-8 bytes, not JS char count).
      // A UTF-16 code unit is at most 3 UTF-8 bytes, so the cheap length check
      // clears all but the largest messages without encoding on this hot path.
      const mayExceed = message.length * 3 > MAX_MESSAGE_SIZE
      const truncatedMessage =
        mayExceed && utf8Length(message) > MAX_MESSAGE_SIZE
          ? truncateToByteLength(message, MAX_MESSAGE_SIZE - 3) + '...'
          : message

      this._logBuffer.push({
        message: truncatedMessage,
        timestamp,
      })

      // Prevent buffer overflow
      if (this._logBuffer.length > MAX_EVENTS_PER_BATCH) {
        this._logBuffer = this._logBuffer.slice(-MAX_EVENTS_PER_BATCH)
      }

      this.startSendTimer()
    } finally {
      release()
    }
  }

  /**
   * Re-queues the events in a failed batch that still have budget and drops the
   * rest. Budget is tracked per event, not per send, so a newly arrived log is
   * not binned on an older batch's last strike once the two travel together.
   * An environmental failure spends wall-clock time; anything else spends an
   * attempt.
   */
  private requeueOrDrop(logs: BufferedEvent[], err: unknown): void {
    const transient = isTransientFailure(err)
    const now = Date.now()

    const retry: BufferedEvent[] = []
    let dropped = 0

    for (const log of logs) {
      if (transient) {
        const since = log.transientSince ?? now
        log.transientSince = since
        if (now - since < MAX_TRANSIENT_MS) {
          retry.push(log)
          continue
        }
      } else {
        const attempts = (log.attempts ?? 0) + 1
        if (attempts < MAX_SEND_ATTEMPTS) {
          log.attempts = attempts
          retry.push(log)
          continue
        }
      }
      dropped += 1
    }

    if (dropped > 0) {
      console.warn(
        transient
          ? `CloudWatch: dropping ${dropped} logs, unsendable for over ${MAX_TRANSIENT_MS}ms`
          : `CloudWatch: dropping ${dropped} logs after ${MAX_SEND_ATTEMPTS} failed sends`,
      )
      // Reported once per drop, so one incident cannot become one Sentry event
      // per attempt — and not at all when the cause is environmental, since a
      // user with no connectivity is not something to action.
      if (!transient) captureException(err)
    }

    if (retry.length === 0) return

    console.warn(`CloudWatch: send failed, re-queueing ${retry.length} logs`)
    this._logBuffer.unshift(...retry)
    // unshift bypasses the cap applied in log(). Re-applying it evicts from the
    // front, so a full buffer discards the batch just re-queued rather than the
    // newer logs behind it.
    if (this._logBuffer.length > MAX_EVENTS_PER_BATCH) {
      console.warn(
        `CloudWatch: buffer full, discarding ${this._logBuffer.length - MAX_EVENTS_PER_BATCH} of the logs just re-queued`,
      )
      this._logBuffer = this._logBuffer.slice(-MAX_EVENTS_PER_BATCH)
    }
  }

  /**
   * Drops a batch AWS refused outright. Reported because resending reproduces
   * the same rejection, so the fault is in how we built the batch.
   */
  private dropRejectedBatch(err: unknown): void {
    console.warn('CloudWatch: batch rejected, dropping logs', err)
    captureException(err)
  }

  /**
   * Classifies a failed send: drop what AWS will never accept, re-queue what
   * might succeed later, and refresh credentials once for what needs them.
   */
  private async handleSendError(
    err: unknown,
    logsToSend: BufferedEvent[],
  ): Promise<void> {
    if (isBatchRejected(err)) {
      this.dropRejectedBatch(err)
      return
    }

    if (hasErrorCode(err, ALREADY_ACCEPTED_CODES)) {
      // AWS has these events, so resending would duplicate them.
      console.warn('CloudWatch: Logs already accepted')
      return
    }

    if (!isCredentialError(err)) {
      this.requeueOrDrop(logsToSend, err)
      return
    }

    console.warn(
      'CloudWatch: Token or signature expired, refreshing credentials',
    )
    try {
      this._cachedCredentials = null
      await this.refreshCredentialsIfNeeded()

      const retryCommand = new PutLogEventsCommand({
        logEvents: toLogEvents(logsToSend),
        logGroupName: this._logGroupName,
        logStreamName: this._logStreamName,
      })
      await this._client.send(retryCommand)
    } catch (retryErr: unknown) {
      if (isBatchRejected(retryErr)) {
        this.dropRejectedBatch(retryErr)
      } else {
        this.requeueOrDrop(logsToSend, retryErr)
      }
    }
  }

  private async sendLogs(): Promise<void> {
    if (this._isSending) {
      return
    }
    this._isSending = true

    try {
      const now = new Date().getTime()
      // Stricter than AWS's floor of 14 days in the past, and a log buffered for
      // a day is not worth sending anyway.
      const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000

      // Events are stamped with Date.now(), so one dated after now means the
      // clock moved backward — an NTP correction after a sleep, typically. AWS
      // rejects the whole batch for an event more than 2 hours ahead of its own
      // clock, so pull those back to now rather than dropping them: the message
      // text carries its own ISO timestamp, so the real time survives.
      let clampedCount = 0
      let logsToSend = this._logBuffer
        .filter((log) => log.timestamp && log.timestamp > twentyFourHoursAgo)
        .map((log) => {
          if (!log.timestamp || log.timestamp <= now) return log
          clampedCount += 1
          return { ...log, timestamp: now }
        })
        .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))

      if (clampedCount > 0) {
        console.warn(
          `CloudWatch: clock moved backward, clamped ${clampedCount} future-dated events to now`,
        )
      }

      // Limit batch size to prevent AWS errors
      let batchSize = 0
      const validLogs: BufferedEvent[] = []

      for (const log of logsToSend) {
        // AWS measures batch size as UTF-8 byte length of message + 26 bytes per-event overhead
        const logSize = utf8Length(log.message ?? '') + AWS_EVENT_OVERHEAD
        if (
          batchSize + logSize > MAX_BATCH_SIZE ||
          validLogs.length >= MAX_EVENTS_PER_BATCH
        ) {
          break
        }
        validLogs.push(log)
        batchSize += logSize
      }

      // Hold the tail that did not fit rather than dropping it, so a burst
      // larger than MAX_BATCH_SIZE drains over the following cycles.
      this._logBuffer = logsToSend.slice(validLogs.length)
      logsToSend = validLogs

      if (logsToSend.length === 0) {
        if (this._sendTimer !== null) {
          clearTimeout(this._sendTimer)
          this._sendTimer = null
        }
        return
      }

      try {
        await this.refreshCredentialsIfNeeded()
        await this.ensureLogStreamExists()

        const params: PutLogEventsCommandInput = {
          logEvents: toLogEvents(logsToSend),
          logGroupName: this._logGroupName,
          logStreamName: this._logStreamName,
        }

        const command = new PutLogEventsCommand(params)
        await this._client.send(command)
      } catch (err: unknown) {
        await this.handleSendError(err, logsToSend)
      } finally {
        if (this._sendTimer !== null) {
          clearTimeout(this._sendTimer)
          this._sendTimer = null
        }
        // Re-armed in the finally so that a throw while classifying the failure
        // cannot leave the sender stopped for good.
        this.startSendTimer()
      }
    } finally {
      this._isSending = false
    }
  }

  public async flush(): Promise<void> {
    if (this._sendTimer !== null) {
      clearTimeout(this._sendTimer)
      this._sendTimer = null
    }

    await this.sendLogs()
  }

  private startSendTimer(): void {
    if (this._sendTimer !== null) return

    this._sendTimer = setTimeout(() => {
      // The handle is spent once the callback runs. Clearing it up front keeps a
      // throw below from leaving a stale non-null handle, which would make every
      // later startSendTimer() a no-op and stop the sender for the session.
      this._sendTimer = null
      void this.sendLogs().catch((err: unknown) => {
        console.warn('CloudWatch: send cycle failed', err)
        this.startSendTimer()
      })
    }, SEND_DELAY)
  }

  private async refreshCredentialsIfNeeded(): Promise<void> {
    const release = await this._mutex.acquire()
    try {
      const bufferTimeMs = 5 * 60 * 1000 // 5 minute buffer
      const now = Date.now()

      // Check if we need new credentials
      const needNewCreds =
        !this._cachedCredentials ||
        !this._cachedCredentials.expiration ||
        // Credentials are already expired
        this._cachedCredentials.expiration.getTime() <= now ||
        // Credentials will expire within the buffer time
        this._cachedCredentials.expiration.getTime() - now <= bufferTimeMs

      // If we need new credentials, fetch them
      if (needNewCreds) {
        this._cachedCredentials = await this._credentialProvider()

        // Update the CloudWatch client with the new credentials
        this._client = new CloudWatchLogsClient({
          region: REGION,
          credentials: this._cachedCredentials,
        })
      }
      // Reported by the caller, not here: this runs once per send cycle, so
      // reporting a persistent outage here produces one Sentry event every
      // SEND_DELAY for as long as it lasts.
    } finally {
      release()
    }
  }

  private async logStreamExists(): Promise<boolean> {
    const command = new DescribeLogStreamsCommand({
      logGroupName: this._logGroupName,
      logStreamNamePrefix: this._logStreamName,
    })
    const response = await this._client.send(command)
    return (
      response.logStreams?.some(
        (stream) => stream.logStreamName === this._logStreamName,
      ) ?? false
    )
  }

  private async createLogStream(): Promise<void> {
    //if the log stream already exists, we don't need to create it and we can move on
    try {
      const command = new CreateLogStreamCommand({
        logGroupName: this._logGroupName,
        logStreamName: this._logStreamName,
      })
      await this._client.send(command)
    } catch (err: unknown) {
      if (
        err instanceof Error &&
        err.name === 'ResourceAlreadyExistsException'
      ) {
        return Promise.resolve()
      } else {
        captureException(err)
      }
    }
  }

  private async ensureLogStreamExists(): Promise<void> {
    if (this._logStreamExists) return

    if (!(await this.logStreamExists())) {
      await this.createLogStream()
    }
    this._logStreamExists = true
  }
}

export default CloudWatchLogger
